/*
  File: src/pages/RekognitionTest.jsx
  Purpose: Manually test the backend /rekognition/detect endpoint.
  Features:
  - Upload an image locally for preview
  - Provide S3 bucket/key and optional thresholds
  - Send POST to {API_BASE}/rekognition/detect
  - Render returned bounding boxes (jerseys, faces, texts) over the preview
  - Show simple analytics: counts and top confidences
*/
import React, { useMemo, useRef, useState } from 'react';
import Navbar from '../components/layout/Navbar';

function normalizeBox(bb) {
    if (!bb) return undefined;
    if (Array.isArray(bb) && bb.length === 4) return bb; // [L,T,W,H] relative 0..1
    const b = bb.BoundingBox || bb.boundingBox || bb.box || bb;
    if (!b) return undefined;
    const L = typeof b.Left === 'number' ? b.Left : (typeof b.left === 'number' ? b.left : b[0]);
    const T = typeof b.Top === 'number' ? b.Top : (typeof b.top === 'number' ? b.top : b[1]);
    const W = typeof b.Width === 'number' ? b.Width : (typeof b.width === 'number' ? b.width : b[2]);
    const H = typeof b.Height === 'number' ? b.Height : (typeof b.height === 'number' ? b.height : b[3]);
    const nums = [L, T, W, H].map((v) => (typeof v === 'number' ? v : undefined));
    if (nums.some((v) => typeof v !== 'number')) return undefined;
    return nums;
}

function normalizeItem(item, kind) {
    const confidence = item.confidence ?? item.Confidence ?? item.ConfidenceScore ?? item.similarity ?? item.c ?? undefined;
    const name = item.name ?? item.Label ?? item.text ?? item.Text ?? item.t ?? undefined;
    // Support multiple possible shapes for the bounding box, including bbox_rel
    const box = normalizeBox(
        item.box ?? item.BoundingBox ?? item.boundingBox ?? item.Location ?? item.bbox_rel
    );
    return { kind, name, confidence, box, rawType: item.type || item.Type };
}

function Box({ box, colorClass, label, position = 'above' }) {
    if (!box || box.length !== 4) return null;
    const [L, T, W, H] = box;
    const left = Math.max(0, Math.min(100, L * 100));
    const top = Math.max(0, Math.min(100, T * 100));
    const width = Math.max(0, Math.min(100, W * 100));
    const height = Math.max(0, Math.min(100, H * 100));
    // Label placement: above (default) or to the left of the box
    const labelPosStyle = position === 'left'
        ? { right: '100%', top: 0, marginRight: 6 }
        : { bottom: '100%', left: 0, marginBottom: 4 };
    return (
        <div className={`absolute ${colorClass}`} style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}>
            {label ? (
                <div
                    className={`absolute px-1.5 py-0.5 rounded text-[11px] font-semibold text-white bg-black/70 border border-white/20 pointer-events-none leading-tight whitespace-pre-wrap`}
                    style={{ ...labelPosStyle, maxWidth: 280, wordBreak: 'break-word' }}
                >{label}</div>
            ) : null}
            <div className={`w-full h-full border-2 ${colorClass}`} />
        </div>
    );
}

export default function RekognitionTest() {
    const [file, setFile] = useState(null);
    const [minLabelConf, setMinLabelConf] = useState(80);
    const [minFaceConf, setMinFaceConf] = useState(80);
    const [minTextConf, setMinTextConf] = useState(80);
    const [previewUrl, setPreviewUrl] = useState('');
    const [modelArn, setModelArn] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const [imgDims, setImgDims] = useState({ w: 0, h: 0 });

    const API_BASE = (process.env.REACT_APP_API_BASE || '/dev');

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        // Revoke previous object URL to avoid memory leaks
        if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch (_) { }
        setFile(f);
        const url = URL.createObjectURL(f);
        setPreviewUrl(url);
        // Clear previous results/overlays and errors when picking a new image
        setResult(null);
        setError('');
    };

    const submit = async () => {
        setError('');
        setResult(null);
        if (!file) {
            setError('Please upload an image file.');
            return;
        }
        setLoading(true);
        try {
            const form = new FormData();
            form.append('image', file);
            form.append('min_label_conf', String(minLabelConf));
            form.append('min_face_conf', String(minFaceConf));
            form.append('min_text_conf', String(minTextConf));
            if (modelArn && modelArn.trim()) form.append('model', modelArn.trim());

            const res = await fetch(`${API_BASE}/rekognition/detect`, {
                method: 'POST',
                headers: { accept: 'application/json' },
                body: form,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setResult(data || {});
        } catch (e) {
            setError(e.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    };

    const items = useMemo(() => {
        if (!result) return [];
        const jerseys = Array.isArray(result.jerseys) ? result.jerseys.map((x) => normalizeItem(x, 'jersey')) : [];
        // Attach absolute boxes for faces if provided as [x1,y1,x2,y2]
        const faces = Array.isArray(result.faces)
            ? result.faces.map((x) => {
                const it = normalizeItem(x, 'face');
                if (Array.isArray(x.face_box) && x.face_box.length === 4) it.absXYXY = x.face_box;
                return it;
            })
            : [];
        const texts = Array.isArray(result.texts) ? result.texts.map((x) => normalizeItem(x, 'text')) : [];
        // Include detected numbers (absolute XYXY) from legacy detections
        const numbers = Array.isArray(result.detections)
            ? result.detections.flatMap((d) => {
                const n = d && d.number;
                if (n && Array.isArray(n.box) && n.box.length === 4) {
                    return [{ kind: 'number', name: n.value, confidence: n.confidence, absXYXY: n.box }];
                }
                return [];
            })
            : [];
        // New teams schema
        const fromTeams = Array.isArray(result.teams)
            ? result.teams.flatMap((t) => {
                const teamName = t?.team || '';
                return (Array.isArray(t?.instances) ? t.instances : []).flatMap((inst) => {
                    const out = [];
                    const j = inst?.jersey;
                    if (j && Array.isArray(j.box) && j.box.length === 4) {
                        const jName = [teamName, j.variant, j.side].filter(Boolean).join(' ');
                        out.push({ kind: 'jersey', name: jName, confidence: j.confidence, absXYXY: j.box });
                    }
                    const n = inst?.number;
                    if (n && Array.isArray(n.box) && n.box.length === 4) {
                        out.push({ kind: 'number', name: n.value, confidence: n.confidence, absXYXY: n.box });
                    }
                    const p = inst?.player;
                    if (p && p.source === 'face_front' && Array.isArray(p.face_box) && p.face_box.length === 4) {
                        out.push({ kind: 'face', name: p.name, confidence: p.similarity, absXYXY: p.face_box });
                    } else if (p && p.source === 'ocr_back' && p.name) {
                        // If OCR back, try to place a text label using hint box if available
                        const hints = Array.isArray(inst?.possible_names) ? inst.possible_names : [];
                        const best = hints.find((h) => (h?.text || h?.Text) === p.name && h?.box_rel_hint);
                        const hint = best?.box_rel_hint;
                        if (hint) {
                            const rel = normalizeBox({ BoundingBox: hint });
                            if (rel) out.push({ kind: 'text', name: p.name, confidence: undefined, box: rel });
                        }
                    }
                    return out;
                });
            })
            : [];
        const unassigned = Array.isArray(result.unassigned_faces)
            ? result.unassigned_faces.map((f) => {
                const it = normalizeItem(f, 'face');
                if (Array.isArray(f.face_box) && f.face_box.length === 4) it.absXYXY = f.face_box;
                return it;
            })
            : [];
        return [...jerseys, ...faces, ...texts, ...numbers, ...fromTeams, ...unassigned];
    }, [result]);

    const counts = useMemo(() => {
        return {
            jerseys: items.filter(i => i.kind === 'jersey').length,
            faces: items.filter(i => i.kind === 'face').length,
            texts: items.filter(i => i.kind === 'text').length,
        };
    }, [items]);

    const analyticsDetails = useMemo(() => {
        const jerseys = Array.isArray(result?.jerseys)
            ? result.jerseys.map((j) => ({
                name: j?.name || j?.Label || 'jersey',
                conf: typeof j?.confidence === 'number' ? j.confidence : (typeof j?.Confidence === 'number' ? j.Confidence : undefined),
            }))
            : [];
        const faces = Array.isArray(result?.faces)
            ? result.faces.filter((f) => !!f?.name).map((f) => ({
                name: f.name,
                conf: typeof f?.similarity === 'number' ? f.similarity : (typeof f?.confidence === 'number' ? f.confidence : undefined),
            }))
            : [];
        const texts = Array.isArray(result?.texts)
            ? result.texts.map((t) => ({
                text: t?.text || t?.Text || '',
                conf: typeof t?.confidence === 'number' ? t.confidence : (typeof t?.Confidence === 'number' ? t.Confidence : undefined),
            }))
            : [];
        const teams = Array.isArray(result?.teams)
            ? result.teams.map((tm) => {
                const team = tm?.team || '';
                const instances = (Array.isArray(tm?.instances) ? tm.instances : []).map((inst) => {
                    const variant = inst?.jersey?.variant || '';
                    const side = inst?.jersey?.side || '';
                    const jerseyLabel = [variant, side].filter(Boolean).join(' ') || 'jersey';
                    const jerseyConf = inst?.jersey?.confidence;
                    const numberVal = inst?.number?.value;
                    const numberConf = inst?.number?.confidence;
                    const playerName = inst?.player?.name;
                    const playerSrc = inst?.player?.source || '';
                    const playerSim = inst?.player?.similarity;
                    const possNames = Array.isArray(inst?.possible_names) ? inst.possible_names.map((p) => p?.text || p?.Text || '') : [];
                    return { variant, side, jerseyLabel, jerseyConf, numberVal, numberConf, playerName, playerSrc, playerSim, possNames };
                });
                return { team, instances };
            })
            : [];
        // Team summaries for compact analytics rendering
        const teamSummaries = teams.map((tm) => {
            const variantCounts = {};
            let topVariant = '';
            let topCount = 0;
            (tm.instances || []).forEach((ins) => {
                const v = ins.variant || '';
                if (!v) return;
                variantCounts[v] = (variantCounts[v] || 0) + 1;
                if (variantCounts[v] > topCount) { topVariant = v; topCount = variantCounts[v]; }
            });
            const players = (tm.instances || [])
                .filter((ins) => !!ins.playerName || !!ins.numberVal)
                .map((ins) => ({
                    name: ins.playerName,
                    number: ins.numberVal,
                    jerseyConf: ins.jerseyConf,
                    numberConf: ins.numberConf,
                    sim: ins.playerSim,
                }));
            return { team: tm.team, kit: topVariant, players };
        });
        const unassignedFaces = Array.isArray(result?.unassigned_faces)
            ? result.unassigned_faces.filter((f) => !!f?.name).map((f) => ({ name: f.name, conf: typeof f?.similarity === 'number' ? f.similarity : undefined }))
            : [];
        return { jerseys, faces, texts, teams, teamSummaries, unassignedFaces };
    }, [result]);

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#0a1628] via-[#1a2448] to-[#0a1628] text-white pt-14 sm:pt-16">
            <Navbar />
            <main className="flex-1">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
                    <div className="text-xs uppercase tracking-wide text-[#4cc3e9] font-semibold">AWS Rekognition Test</div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Controls */}
                        <div className="lg:col-span-1 space-y-3 bg-gradient-to-br from-[#1a2448]/70 to-[#1a2448]/50 rounded-xl p-4 border border-[#4cc3e9]/20">
                            <div className="text-sm font-semibold">Input</div>
                            <div className="text-[12px] text-white/70">Upload an image, adjust thresholds, and analyze with Rekognition.</div>

                            <div className="grid grid-cols-3 gap-2">
                                <div>
                                    <label className="block text-[11px] text-white/70 mb-1">Label conf</label>
                                    <input type="number" className="w-full rounded-lg px-2 py-1 bg-[#0f1b33] border border-[#4cc3e9]/20" value={minLabelConf} onChange={(e) => setMinLabelConf(e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-[11px] text-white/70 mb-1">Face conf</label>
                                    <input type="number" className="w-full rounded-lg px-2 py-1 bg-[#0f1b33] border border-[#4cc3e9]/20" value={minFaceConf} onChange={(e) => setMinFaceConf(e.target.value)} />
                                </div>
                                <div>
                                    <label className="block text-[11px] text-white/70 mb-1">Text conf</label>
                                    <input type="number" className="w-full rounded-lg px-2 py-1 bg-[#0f1b33] border border-[#4cc3e9]/20" value={minTextConf} onChange={(e) => setMinTextConf(e.target.value)} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[12px] text-white/80 mb-1">Model ARN (optional)</label>
                                <input
                                    type="text"
                                    value={modelArn}
                                    onChange={(e) => setModelArn(e.target.value)}
                                    placeholder="arn:aws:rekognition:region:acct-id:project/.../version/..."
                                    className="w-full rounded-lg px-2 py-1 bg-[#0f1b33] border border-[#4cc3e9]/20 text-[12px] placeholder-white/40"
                                />
                            </div>

                            <div>
                                <label className="block text-[12px] text-white/80 mb-1">Upload image</label>
                                <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="block w-full text-[12px]" />
                            </div>

                            <button onClick={submit} disabled={loading} className="w-full rounded-lg py-2 mt-1 bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium border border-[#9060ff]/40 transition-all duration-200 hover:scale-[1.02]">
                                {loading ? 'Analyzing…' : 'Analyze'}
                            </button>

                            {error && <div className="mt-2 text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{error}</div>}

                            {result && (
                                <div className="mt-3 text-[12px] space-y-2">
                                    <div className="text-[#4cc3e9] font-semibold">Analytics</div>
                                    {result.image && (
                                        <div className="text-white/70">Image: <span className="font-semibold text-white">{result.image.filename || 'uploaded'}</span>{typeof result.image.size_bytes === 'number' ? ` • ${result.image.size_bytes} bytes` : ''}</div>
                                    )}
                                    <div>Jerseys: <span className="font-semibold">{counts.jerseys}</span></div>
                                    {analyticsDetails.jerseys.length > 0 && (
                                        <ul className="list-disc ml-5 text-white/80">
                                            {analyticsDetails.jerseys.map((j, i) => (
                                                <li key={`j-${i}`}>{(j.name || 'jersey').replace(/_/g, ' ')}{typeof j.conf === 'number' ? ` • ${j.conf.toFixed(1)}%` : ''}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {analyticsDetails.teamSummaries && analyticsDetails.teamSummaries.length > 0 && (
                                        <div className="mt-2">
                                            <div className="text-[#4cc3e9] font-semibold">Teams</div>
                                            <div className="grid grid-cols-1 gap-2">
                                                {analyticsDetails.teamSummaries.map((tm, ti) => (
                                                    <div key={`ts-${ti}`} className="rounded-lg border border-[#4cc3e9]/20 bg-gradient-to-br from-[#1a2448]/60 to-[#1a2448]/40 p-3">
                                                        <div className="flex items-center justify-between">
                                                            <div className="text-sm font-semibold capitalize">{tm.team}</div>
                                                            {tm.kit ? (
                                                                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#4cc3e9]/20 text-[#4cc3e9] border border-[#4cc3e9]/30">{tm.kit.replace(/_/g, ' ')}</span>
                                                            ) : null}
                                                        </div>
                                                        <div className="mt-2 space-y-1">
                                                            {(tm.players || []).length > 0 ? (
                                                                tm.players.map((p, pi) => (
                                                                    <div key={`p-${ti}-${pi}`} className="flex items-center justify-between text-[12px]">
                                                                        <div className="flex items-center gap-2">
                                                                            {p.name ? (<span className="capitalize font-semibold">{p.name}</span>) : null}
                                                                            {p.number ? (
                                                                                <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded border border-white/20 bg-white/10 text-[10px]">#{p.number}</span>
                                                                            ) : null}
                                                                        </div>
                                                                        <div className="flex items-center gap-2 text-[10px] text-white/70">
                                                                            {typeof p.sim === 'number' && p.name ? (<span title="Face similarity">face {p.sim.toFixed(1)}%</span>) : null}
                                                                            {typeof p.jerseyConf === 'number' ? (<span title="Jersey confidence">jersey {p.jerseyConf.toFixed(1)}%</span>) : null}
                                                                            {typeof p.numberConf === 'number' ? (<span title="Number confidence"># {p.numberConf.toFixed(1)}%</span>) : null}
                                                                        </div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="text-white/60 text-[12px]">No players resolved</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div>Faces: <span className="font-semibold">{counts.faces}</span></div>
                                    {analyticsDetails.faces.length > 0 && (
                                        <ul className="list-disc ml-5 text-white/80">
                                            {analyticsDetails.faces.map((f, i) => (
                                                <li key={`f-${i}`}>{f.name}{typeof f.conf === 'number' ? ` • ${f.conf.toFixed(1)}%` : ''}</li>
                                            ))}
                                        </ul>
                                    )}
                                    {analyticsDetails.unassignedFaces && analyticsDetails.unassignedFaces.length > 0 && (
                                        <div className="mt-1 text-white/80">
                                            <div className="font-semibold">Unassigned faces</div>
                                            <ul className="list-disc ml-5">
                                                {analyticsDetails.unassignedFaces.map((f, i) => (
                                                    <li key={`uf-${i}`}>{f.name}{typeof f.conf === 'number' ? ` • ${f.conf.toFixed(1)}%` : ''}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div>Texts: <span className="font-semibold">{counts.texts}</span></div>
                                    {analyticsDetails.texts.length > 0 && (
                                        <ul className="list-disc ml-5 text-white/80">
                                            {analyticsDetails.texts.map((t, i) => (
                                                <li key={`t-${i}`}>{t.text}{typeof t.conf === 'number' ? ` • ${t.conf.toFixed(1)}%` : ''}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Preview */}
                        <div className="lg:col-span-2">
                            <div key={previewUrl || 'noimg'} className="relative w-full aspect-[16/9] bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl overflow-hidden border border-[#4cc3e9]/30">
                                {previewUrl ? (
                                    <img
                                        src={previewUrl}
                                        alt="preview"
                                        className="w-full h-full object-contain bg-black relative z-0"
                                        onLoad={(e) => {
                                            const img = e.currentTarget;
                                            setImgDims({ w: img.naturalWidth || 0, h: img.naturalHeight || 0 });
                                        }}
                                    />
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-white/50 text-sm">Upload an image to preview overlays</div>
                                )}
                                <div className="absolute inset-0 pointer-events-none z-10">
                                    {(() => {
                                        // Helper: compute relative box
                                        const toRel = (it) => {
                                            if (Array.isArray(it.box) && it.box.length === 4) return it.box;
                                            if (Array.isArray(it.absXYXY) && imgDims.w && imgDims.h) {
                                                const [x1, y1, x2, y2] = it.absXYXY;
                                                const L = x1 / imgDims.w;
                                                const T = y1 / imgDims.h;
                                                const W = Math.max(0, (x2 - x1) / imgDims.w);
                                                const H = Math.max(0, (y2 - y1) / imgDims.h);
                                                return [L, T, W, H];
                                            }
                                            return null;
                                        };
                                        const iou = (a, b) => {
                                            if (!a || !b) return 0;
                                            const [ax, ay, aw, ah] = a; // relative
                                            const [bx, by, bw, bh] = b;
                                            const ax2 = ax + aw, ay2 = ay + ah;
                                            const bx2 = bx + bw, by2 = by + bh;
                                            const ix1 = Math.max(ax, bx), iy1 = Math.max(ay, by);
                                            const ix2 = Math.min(ax2, bx2), iy2 = Math.min(ay2, by2);
                                            const iw = Math.max(0, ix2 - ix1), ih = Math.max(0, iy2 - iy1);
                                            const inter = iw * ih;
                                            const union = aw * ah + bw * bh - inter;
                                            return union > 0 ? inter / union : 0;
                                        };

                                        const jerseys = [];
                                        const numbers = [];
                                        const faces = [];
                                        const texts = [];
                                        items.forEach((it, idx) => {
                                            const rel = toRel(it);
                                            if (!rel) return;
                                            const entry = { ...it, rel, _idx: idx };
                                            if (it.kind === 'jersey') jerseys.push(entry);
                                            else if (it.kind === 'number') numbers.push(entry);
                                            else if (it.kind === 'face') faces.push(entry);
                                            else if (it.kind === 'text') texts.push(entry);
                                        });

                                        // Combine number with nearest overlapping jersey
                                        const usedNum = new Set();
                                        const render = [];
                                        jerseys.forEach(j => {
                                            let match = null;
                                            let best = 0;
                                            numbers.forEach(n => {
                                                if (usedNum.has(n._idx)) return;
                                                const ov = iou(j.rel, n.rel);
                                                if (ov > 0.15 && ov > best) { best = ov; match = n; }
                                            });
                                            const labelNode = (
                                                <>
                                                    <div>{j.name || 'jersey'}</div>
                                                    {match && (match.name !== undefined && match.name !== null) ? (
                                                        <div>#{String(match.name)}</div>
                                                    ) : null}
                                                </>
                                            );
                                            render.push(
                                                <Box key={`j-${j._idx}`} box={j.rel} colorClass={'border-yellow-400'} label={labelNode} />
                                            );
                                            if (match) usedNum.add(match._idx);
                                        });

                                        // Render remaining numbers
                                        numbers.forEach(n => {
                                            if (usedNum.has(n._idx)) return;
                                            const node = (<div>#{String(n.name)}</div>);
                                            render.push(
                                                <Box key={`n-${n._idx}`} box={n.rel} colorClass={'border-emerald-400'} label={node} />
                                            );
                                        });

                                        // Faces and texts, without percentages
                                        faces.forEach(f => {
                                            const node = (<div>{f.name || ''}</div>);
                                            render.push(
                                                <Box key={`f-${f._idx}`} box={f.rel} colorClass={'border-cyan-400'} label={node} position="left" />
                                            );
                                        });
                                        texts.forEach(t => {
                                            const node = (<div>{t.name || 'text'}</div>);
                                            render.push(
                                                <Box key={`t-${t._idx}`} box={t.rel} colorClass={'border-fuchsia-400'} label={node} />
                                            );
                                        });

                                        return render;
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
